import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import toastError from "../../../errors/toastError";
import api from "../../../services/api";
import { i18n } from "../../../translate/i18n";

export default function QueueModal({ open, template, onClose, onSaved }) {
  const [queues, setQueues] = useState([]);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    let isMounted = true;
    const abortController = new AbortController();

    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/queue", { signal: abortController.signal });
        if (!isMounted) return;
        setQueues(data || []);
        setSelectedQueueIds(template?.queueIds || []);
      } catch (err) {
        if (!isMounted || err.name === "AbortError") return;
        toastError(err);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [open, template]);

  const handleToggleQueue = (queueId) => {
    setSelectedQueueIds((prev) =>
      prev.includes(queueId)
        ? prev.filter((id) => id !== queueId)
        : [...prev, queueId]
    );
  };

  const handleSave = async () => {
    if (!template?.dbId) return;

    setSaving(true);
    try {
      const { data } = await api.post("/whatsappTemplate/associateQueues", {
        dbId: template.dbId,
        queueIds: selectedQueueIds,
      });
      toast.success(
        data?.message ? i18n.t(data.message) : i18n.t("generalMessages.SUCCESSFULLY_SAVED")
      );
      onSaved?.(data?.whatsappTemplate);
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {i18n.t("connections.whatsapp.template.queuesModal.title", {
          name: template?.displayName || template?.name || "",
        })}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <CircularProgress size={24} />
        ) : (
          <FormControl component="fieldset" fullWidth>
            <FormGroup>
              {queues.map((queue) => (
                <FormControlLabel
                  key={queue.id}
                  control={
                    <Checkbox
                      checked={selectedQueueIds.includes(queue.id)}
                      onChange={() => handleToggleQueue(queue.id)}
                      color="primary"
                    />
                  }
                  label={queue.name}
                />
              ))}
            </FormGroup>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {i18n.t("connections.whatsapp.template.modal.buttons.cancel")}
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={saving || loading}>
          {saving ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            i18n.t("connections.whatsapp.template.modal.buttons.save")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
